import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { MenuTypes } from '@ori-events/toggle-menu-flyout';
import * as UserAction from '@ori-events/user-action';
import { Clear, Search } from '@ori-ui/icons';
import { debounce } from '@ori-ui/mui';
import { useWindowSize } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useCallback, useState } from 'react';
import { useDebounce, useThrottledCallback } from 'use-debounce';

import { SEARCH_INPUT_HEIGHT, SEARCH_THROTTLE_TIME, START_SEARCH } from '../../constants';
import { useAppConfig, useTopAreaBanner } from '../../context';
import { useActiveMenu, useSearchInput } from '../../context/navigation';
import { useIsMobile } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { dispatchToggleMenu } from '../../utils';
import type { SearchInputRootProps } from './styles';
import { InputBackdrop, SearchInputBox, SearchInputClear, SearchInputInput, SearchInputRoot } from './styles';
import { onEnterPressHandler } from './utils';

type SearchInputProps = Pick<SearchInputRootProps, 'showOnlyOn'>;
const SCROLL_THRESHOLD = SEARCH_INPUT_HEIGHT + 5;

export const SearchInput: FC<SearchInputProps> = ({ showOnlyOn }) => {
  const { showSearchOnScroll } = useAppConfig();
  const { visible: isTopBannerVisible } = useTopAreaBanner();
  const { activeMenu, hideSearchResultsTab, toggleActiveMenu } = useActiveMenu();
  const {
    hide,
    show,
    isFocused,
    isVisible,
    focus,
    blur,
    search,
    clearResults,
    value,
    isManuallyOpened,
    setValue,
    manualHide,
  } = useSearchInput();
  const { sync } = useWindowSize();
  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.SearchResults;
  const isMobile = useIsMobile();
  const { translations } = useTranslations();
  const [prevScrollY, setPrevScrollY] = useState(0);
  const { getTestId } = useTestId();
  const [debouncedValue] = useDebounce(value, 350);
  const throttledAnalytics = useThrottledCallback(() => UserAction.dispatch(START_SEARCH), SEARCH_THROTTLE_TIME);
  const searchPlaceholder = showOnlyOn === 'mobile' ? translations.search : translations.searchDesktop;
  const onFocusHandler = useCallback(() => {
    focus();
    sync();
  }, [focus, sync]);

  const onChangeHandler = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const isValueEmpty = target.value.length === 0;
      if (isValueEmpty) {
        toggleActiveMenu('');
        clearResults();
      }
      setValue(target.value);
      throttledAnalytics();
      if (!isValueEmpty && !isMenuActive) {
        dispatchToggleMenu(ToggleMenuFlyout.MenuTypes.SearchResults);
      }
    },
    [setValue, throttledAnalytics, isMenuActive, toggleActiveMenu, clearResults],
  );

  useEffect(() => {
    const showWithoutFocus = () => {
      show();
      blur();
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const difference = currentScrollY - prevScrollY;
      if (difference > SCROLL_THRESHOLD && isMobile && !isManuallyOpened) {
        hide();
        hideSearchResultsTab();
      } else if (difference < -SCROLL_THRESHOLD && isMobile) {
        showWithoutFocus();
      }
      setPrevScrollY(currentScrollY);
    };
    const handleScrollDebounced = debounce(handleScroll, 50);
    if (showSearchOnScroll) {
      window.addEventListener('scroll', handleScrollDebounced);
    }

    return () => window.removeEventListener('scroll', handleScrollDebounced);
  }, [isMobile, show, hide, blur, hideSearchResultsTab, isManuallyOpened, prevScrollY, showSearchOnScroll]);

  const closeSearch = useCallback(() => {
    manualHide();
    hide();
    hideSearchResultsTab();
  }, [hide, hideSearchResultsTab, manualHide]);

  const clearInput = useCallback(() => {
    setValue('');
    clearResults();
    toggleActiveMenu(MenuTypes.SearchInput);
    blur();
  }, [blur, clearResults, setValue, toggleActiveMenu]);

  const handleOnBlur = useCallback(() => {
    if (!value) {
      blur();
    }
    sync();
  }, [blur, sync, value]);

  useEffect(() => {
    search(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps  -- we want to fetch results ONLY when input value changes (with debounce)
  }, [debouncedValue]);

  const isVisibleOnMobile = showOnlyOn === 'mobile' && isVisible;

  return (
    <SearchInputRoot
      isTopBannerVisible={isTopBannerVisible}
      isVisibleOnMobile={isVisibleOnMobile}
      showSearchOnScroll={showSearchOnScroll}
      showOnlyOn={showOnlyOn}
      data-testid={getTestId(showOnlyOn === 'mobile' ? 'search-input-root-mobile' : 'search-input-root-desktop')}
    >
      <SearchInputBox
        isFocused={isFocused}
        data-testid={getTestId('search-input-box')}
      >
        <Search
          fontSize="inherit"
          data-testid={getTestId('search')}
        />
        <SearchInputInput
          autoFocus={isManuallyOpened}
          type="text"
          placeholder={searchPlaceholder}
          value={value}
          data-testid={getTestId('search-input')}
          onFocus={onFocusHandler}
          onChange={onChangeHandler}
          onBlur={handleOnBlur}
          onKeyDown={onEnterPressHandler}
        />
        <SearchInputClear
          disableRipple
          data-testid={getTestId('search-input-clear')}
          isFocused={isFocused}
          onClick={clearInput}
        >
          <Clear fontSize="inherit" />
        </SearchInputClear>
      </SearchInputBox>
      {isMobile && !isMenuActive && isManuallyOpened ? (
        <InputBackdrop
          open
          data-testid={getTestId('backdrop')}
          onClick={closeSearch}
        />
      ) : null}
    </SearchInputRoot>
  );
};
